// Member notes for one project (architecture sprint-12 §9.2; sprint-15 §9.1). Used by the project detail Notes tab
// and the Oversight Notes page. Sprint 15, the fixed notes rule: every Supervisor of a notes-on project (and every
// Admin) lists, adds and exports every note; nothing depends on per-project switches any more.
//   - "All · To review (n)": To review lists the reviewable self-notes (no dates), grouped by shift, each group
//     with "Approve all (n)"; unlinked notes come after the groups.
//   - A shift filter (from a review notification) shows a banner, Approve all and "Show all notes".
//   - Each card: "Self-reported", "Pending review" / "Approved by …", Approve (pending, not about me), and the
//     ⋯ menu with Edit / Delete whenever the note is editable for me (History stays Admin-only).
// The API is the arbiter (§5.3); canEdit is re-checked against the 24 h window every minute.
import { todayIn } from '@shared/calendar';
import { STAFF_EDIT_WINDOW_MS } from '@shared/note-rules';
import { Check, Download, MoreHorizontal, NotebookPen } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { LoadMoreButton } from '@/components/LoadMoreButton';
import { RangePicker, type RangePreset } from '@/components/RangePicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useMe } from '@/hooks/use-me';
import { useDeleteNote, useExportNotes, useNotes, useNoteSubjects } from '@/hooks/use-notes';
import { useApproveNotes } from '@/hooks/use-notes-review';
import { formatDateTime, formatLocalDate } from '@/lib/format';
import { presetRange, type DateRange } from '@/lib/report-model';
import { dateIn, timeRange } from '@/lib/shift-format';
import { useNowTick } from '@/lib/use-now';
import type { Note, NoteFilters } from '@/types/notes';
import type { OversightProject } from '@/types/settings';
import { NoteDialog } from './NoteDialog';
import { NoteHistoryDialog } from './NoteHistoryDialog';

export const NOTES_OFF_HINT = 'Turn on Member notes in Settings to add notes.';
export const VISIBILITY_HINT = 'Notes are visible to every Supervisor of this project and to Admins. Members never see them.';
export const SELF_REPORTED = 'Self-reported';
export const PENDING_REVIEW = 'Pending review';
export const SHOW_ALL_NOTES = 'Show all notes';
export const FORMER_MEMBER_SUFFIX = ' (former member)';
const CANEDIT_TICK_MS = 60_000;

const PRESET_KEYS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '3m', label: '3 months' },
] as const;

type ExportFormat = 'csv' | 'xlsx';
type View = 'all' | 'review';

interface NotesPanelProps {
  project: Pick<OversightProject, 'id' | 'name' | 'caps' | 'notesEnabled' | 'pendingReviewCount'>;
  timezone: string;
  /** Oversight Notes page: the URL drives the To review view and the shift filter. */
  review?: boolean;
  shiftId?: string | null;
  onReviewChange?: (review: boolean) => void;
  onClearShift?: () => void;
}

/** Sprint 13 (§13): the linked shift in the viewer's timezone, e.g. "Shift 7 Nov 22:00 – 06:00 (voided)". */
export function noteShiftText(shift: NonNullable<Note['shift']>, tz: string): string {
  const day = formatLocalDate(dateIn(shift.startedAt, tz));
  return `Shift ${day} ${timeRange(shift.startedAt, shift.endedAt, tz).replace('–', ' – ')}${shift.voided ? ' (voided)' : ''}`;
}

/** The ⋯ menu: canEdit from the API, re-checked against the 24 h staff window as time passes. */
export function stillEditable(note: Note, isAdmin: boolean, wallNowMs: number): boolean {
  if (note.deleted || note.canEdit !== true) return false;
  if (isAdmin || note.review?.status === 'pending') return true;
  return wallNowMs < Date.parse(note.createdAt) + STAFF_EDIT_WINDOW_MS;
}

/** Pending notes I may approve (never a note about myself). */
export function approvableIds(notes: readonly Note[], meId: string): string[] {
  return notes.filter((n) => n.review?.status === 'pending' && !n.deleted && n.subject.id !== meId).map((n) => n.id);
}

interface ShiftGroup {
  key: string;
  title: string;
  notes: Note[];
}

/** To review: one group per shift ("{member} · {date} {HH:mm}–{HH:mm}"), then the unlinked notes. */
export function groupByShift(notes: readonly Note[], tz: string): { groups: ShiftGroup[]; unlinked: Note[] } {
  const groups = new Map<string, ShiftGroup>();
  const unlinked: Note[] = [];
  for (const n of notes) {
    if (!n.shift) {
      unlinked.push(n);
      continue;
    }
    const g = groups.get(n.shift.id) ?? {
      key: n.shift.id,
      title: `${n.subject.name} · ${formatLocalDate(dateIn(n.shift.startedAt, tz))} ${timeRange(n.shift.startedAt, n.shift.endedAt, tz)}`,
      notes: [],
    };
    g.notes.push(n);
    groups.set(n.shift.id, g);
  }
  return { groups: [...groups.values()], unlinked };
}

function ReviewChip({ note }: { note: Note }): JSX.Element | null {
  if (!note.review) return null;
  if (note.review.status === 'pending') return <Badge variant="warning">{PENDING_REVIEW}</Badge>;
  const by = note.review.reviewedBy?.name ?? '';
  const at = note.review.reviewedAt ? ` · ${formatDateTime(note.review.reviewedAt)}` : '';
  return <Badge variant="success">{`Approved by ${by}${at}`}</Badge>;
}

function NoteCard({
  note,
  isAdmin,
  meId,
  timezone,
  wallNowMs,
  approving,
  onApprove,
  onEdit,
  onDelete,
  onHistory,
}: {
  note: Note;
  isAdmin: boolean;
  meId: string;
  timezone: string;
  wallNowMs: number;
  approving: boolean;
  onApprove: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onHistory: () => void;
}): JSX.Element {
  const editable = stillEditable(note, isAdmin, wallNowMs);
  const canApprove = note.review?.status === 'pending' && !note.deleted && note.subject.id !== meId;
  const showMenu = editable || isAdmin;
  return (
    <li className="card space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="flex flex-wrap items-center gap-2 text-sm text-foreground">
          <span className="font-mono tabular-nums">{formatLocalDate(note.date)}</span>
          <span>
            {note.subject.name}
            {!note.subject.current && <span className="text-muted-foreground">{FORMER_MEMBER_SUFFIX}</span>}
          </span>
          {note.kind === 'self' && <Badge variant="info">{SELF_REPORTED}</Badge>}
          <ReviewChip note={note} />
          {note.edited && <Badge>Edited</Badge>}
          {note.deleted && <Badge variant="danger">Deleted</Badge>}
        </p>
        <div className="flex items-center gap-1">
          {canApprove && (
            <Button size="sm" variant="outline" disabled={approving} onClick={onApprove} aria-label={`Approve the note about ${note.subject.name} on ${note.date}`}>
              <Check aria-hidden="true" />
              Approve
            </Button>
          )}
          {showMenu && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={`Actions for the note about ${note.subject.name} on ${note.date}`}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {editable && <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>}
                {editable && <DropdownMenuItem onSelect={onDelete}>Delete</DropdownMenuItem>}
                {isAdmin && <DropdownMenuItem onSelect={onHistory}>History</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {note.shift && <p className="text-xs text-muted-foreground">{noteShiftText(note.shift, timezone)}</p>}
      <p className="whitespace-pre-wrap text-sm text-foreground">{note.description}</p>
      <p className="text-xs text-muted-foreground">
        Logged by {note.author.name} · {formatDateTime(note.createdAt)}
      </p>
    </li>
  );
}

export function NotesPanel({ project, timezone, review: reviewProp, shiftId: shiftProp, onReviewChange, onClearShift }: NotesPanelProps): JSX.Element {
  const { caps } = project;
  const { data: me } = useMe();
  const meId = me?.id ?? '';
  const isAdmin = caps.editNotes;
  const canList = caps.readNotes;
  const addDisabled = isAdmin && !project.notesEnabled;
  const today = todayIn(timezone);
  const switchId = useId();
  // Re-evaluates the 24 h edit window once a minute (the wall clock only gates showing the menu; the API decides).
  useNowTick(CANEDIT_TICK_MS);
  const wallNowMs = Date.now();

  const [range, setRange] = useState<DateRange>(() => presetRange('30d', today));
  const [userId, setUserId] = useState<string>('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [reviewState, setReviewState] = useState(false);
  const [shiftState, setShiftState] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Note | null>(null);
  const [deleting, setDeleting] = useState<Note | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);

  const review = reviewProp ?? reviewState;
  const shiftId = shiftProp !== undefined ? shiftProp : shiftState;
  const view: View = review ? 'review' : 'all';

  const filters: NoteFilters = {
    from: range.from,
    to: range.to,
    userId: userId || null,
    includeDeleted: isAdmin && includeDeleted && !review && !shiftId,
    review,
    shiftId,
  };
  const subjects = useNoteSubjects(project.id);
  const notes = useNotes(project.id, filters, canList);
  const remove = useDeleteNote(project.id);
  const approve = useApproveNotes(project.id);
  const exporter = useExportNotes(project.id, project.name);
  const presets: RangePreset[] = PRESET_KEYS.map((p) => ({ key: p.key, label: p.label, range: presetRange(p.key, today) }));
  const dialogOpen = adding || editing !== null;
  const pendingCount = project.pendingReviewCount ?? 0;

  function setView(next: View): void {
    if (onReviewChange) onReviewChange(next === 'review');
    else setReviewState(next === 'review');
  }

  function clearShift(): void {
    if (onClearShift) onClearShift();
    else setShiftState(null);
  }

  function runExport(format: ExportFormat): void {
    setExporting(format);
    exporter.mutate({ filters: { ...filters, review: false, shiftId: null }, format }, { onSettled: () => setExporting(null) });
  }

  function approveIds(ids: string[], label: string): void {
    if (ids.length === 0) return;
    approve.mutate(ids, { onSuccess: (r) => toast.success(r.approvedCount === 1 ? 'Note approved.' : `${label}: ${r.approvedCount} approved.`) });
  }

  const items = notes.data?.pages.flatMap((p) => p.items) ?? [];
  const card = (n: Note): JSX.Element => (
    <NoteCard
      key={n.id}
      note={n}
      isAdmin={isAdmin}
      meId={meId}
      timezone={timezone}
      wallNowMs={wallNowMs}
      approving={approve.isPending}
      onApprove={() => approveIds([n.id], 'Approve')}
      onEdit={() => setEditing(n)}
      onDelete={() => setDeleting(n)}
      onHistory={() => setHistoryId(n.id)}
    />
  );

  const shiftNote = shiftId ? items.find((n) => n.shift?.id === shiftId) : undefined;
  const shiftApprovable = approvableIds(items, meId);

  let list: JSX.Element;
  if (notes.isLoading) list = <Skeleton className="h-32 w-full" />;
  else if (items.length === 0) list = <EmptyState variant="card" title={review ? 'Nothing to review.' : shiftId ? 'No notes on this shift.' : 'No notes in this range.'} />;
  else if (review && !shiftId) {
    const { groups, unlinked } = groupByShift(items, timezone);
    list = (
      <div className="space-y-6">
        {groups.map((g) => {
          const ids = approvableIds(g.notes, meId);
          return (
            <section key={g.key} aria-label={g.title} className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm text-foreground">{g.title}</h3>
                {ids.length > 0 && (
                  <Button size="sm" variant="outline" disabled={approve.isPending} onClick={() => approveIds(ids, 'Approve all')}>
                    <Check aria-hidden="true" />
                    {`Approve all (${ids.length})`}
                  </Button>
                )}
              </div>
              <ul className="space-y-3">{g.notes.map(card)}</ul>
            </section>
          );
        })}
        {unlinked.length > 0 && (
          <section aria-label="Not linked to a shift" className="space-y-2">
            {groups.length > 0 && <h3 className="text-sm text-foreground">Not linked to a shift</h3>}
            <ul className="space-y-3">{unlinked.map(card)}</ul>
          </section>
        )}
        <LoadMoreButton hasMore={notes.hasNextPage} loading={notes.isFetchingNextPage} onClick={() => void notes.fetchNextPage()} />
      </div>
    );
  } else {
    list = (
      <>
        <ul className="space-y-3">{items.map(card)}</ul>
        <LoadMoreButton hasMore={notes.hasNextPage} loading={notes.isFetchingNextPage} onClick={() => void notes.fetchNextPage()} />
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {caps.addNotes && (
          <Button size="sm" disabled={addDisabled} onClick={() => setAdding(true)}>
            <NotebookPen aria-hidden="true" />
            Add note
          </Button>
        )}
        {caps.exportNotes && canList && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting !== null}>
                <Download aria-hidden="true" />
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onSelect={() => runExport('csv')}>CSV</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => runExport('xlsx')}>XLSX</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {isAdmin && canList && !review && !shiftId && (
          <div className="ml-auto flex items-center gap-2">
            <span id={switchId} className="text-sm text-foreground">
              Show deleted
            </span>
            <Switch aria-labelledby={switchId} checked={includeDeleted} onCheckedChange={setIncludeDeleted} />
          </div>
        )}
      </div>
      {addDisabled && <p className="text-xs text-muted-foreground">{NOTES_OFF_HINT}</p>}
      <p className="text-xs text-muted-foreground">{VISIBILITY_HINT}</p>

      {canList && (
        <>
          {shiftId ? (
            <div className="card flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-foreground">
                {shiftNote?.shift
                  ? `Notes from ${shiftNote.subject.name}'s shift (${timeRange(shiftNote.shift.startedAt, shiftNote.shift.endedAt, timezone)})`
                  : 'Notes from one shift'}
              </p>
              <div className="flex flex-wrap gap-2">
                {shiftApprovable.length > 0 && (
                  <Button size="sm" disabled={approve.isPending} onClick={() => approveIds(shiftApprovable, 'Approve all')}>
                    <Check aria-hidden="true" />
                    {`Approve all (${shiftApprovable.length})`}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={clearShift}>
                  {SHOW_ALL_NOTES}
                </Button>
              </div>
            </div>
          ) : (
            <SegmentedControl<View>
              aria-label="Which notes"
              value={view}
              onChange={setView}
              options={[
                { value: 'all', label: 'All' },
                { value: 'review', label: `To review (${pendingCount})` },
              ]}
            />
          )}
          {!review && !shiftId && (
            <>
              <RangePicker from={range.from} to={range.to} presets={presets} onChange={setRange} />
              <div className="w-full max-w-xs space-y-1">
                <Label htmlFor="notes-member">Member</Label>
                <NativeSelect id="notes-member" value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <option value="">Everyone</option>
                  {(subjects.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.current ? s.name : `${s.name}${FORMER_MEMBER_SUFFIX}`}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </>
          )}
          {list}
        </>
      )}

      <NoteDialog
        projectId={project.id}
        open={dialogOpen}
        onOpenChange={(o) => {
          if (o) return;
          setAdding(false);
          setEditing(null);
        }}
        timezone={timezone}
        subjects={subjects.data ?? []}
        note={editing}
        canLinkShift={isAdmin || caps.viewAttendance}
        subjectLocked={!isAdmin || editing?.kind === 'self'}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this note?"
        body="It disappears from the notes list. Its history stays visible to Admins."
        confirmLabel="Delete"
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.success('Note deleted.');
              setDeleting(null);
            },
            onError: () => setDeleting(null),
          })
        }
      />
      <NoteHistoryDialog projectId={project.id} noteId={historyId} onClose={() => setHistoryId(null)} />
    </div>
  );
}
